import { isPriceSliderFilter } from 'utils/price-filter-utils';

import { FullPropertySearch, PropertySearch } from '__generated__/api';
import { PillProps } from 'components/search/playback/playback-filter-pill';
import * as flatMap from 'flatmap';

export const createSearchAndFilterDisplayOptions = (): any => ({
  filters: {
    displayFields: [
      'sort',
      'commissionTier',
      'hotelName',
      'rewards',
      'popularFilter',
      'star',
      'price',
      'guestRating',
      'paymentType',
      'propertyStyle',
      'lodging',
      'bedroomFilter',
      'regionId',
      'poi',
      'amenities',
      'accessibility',
      'travelerType',
      'deals'
    ]
  }
});

export enum SortAndFilterDisplayField {
  SORT = 'sort',
  HOTEL_NAME = 'hotelName',
  REWARDS = 'rewards',
  STAR = 'star',
  PRICE = 'price',
  GUEST_RATING = 'guestRating',
  LODGING = 'lodging',
  REGION_ID = 'regionId',
  AMENITIES = 'amenities',
  TRAVELER_TYPE = 'travelerType',
  PAYMENT_TYPE = 'paymentType',
  DEALS = 'deals',
  PROPERTY_STYLE = 'propertyStyle',
  BEDROOM_FILTER = 'bedroomFilter'
}

export const mapSortAndFilter = (propertySearch: FullPropertySearch.PropertySearch | PropertySearch.PropertySearch): any => {
  const sortAndFilter: FullPropertySearch.SortAndFilter | PropertySearch.PropertySearch | null = propertySearch.sortAndFilter;

  return {
    defaults: getDefaults(sortAndFilter),
    options: getOptions(sortAndFilter),
    pills: getPills(sortAndFilter)
  };
};

export const getSortAndFilterOption = (field: SortAndFilterDisplayField, sortAndFilter: any): PropertySearch._SortAndFilter | null => {
  if (sortAndFilter && sortAndFilter.options) {
    const result = sortAndFilter.options[field];

    return result ? result : null;
  }

  return null;
};

const getDefaults = (sortAndFilter: PropertySearch.SortAndFilter): any => ({
  regionId: sortAndFilter && sortAndFilter.defaults && sortAndFilter.defaults.regionId,
  sort: sortAndFilter && sortAndFilter.defaults && sortAndFilter.defaults.sort
});

const getOptions = (sortAndFilter: PropertySearch.SortAndFilter): any => {
  const appliedFilterCount = sortAndFilter && sortAndFilter.options && sortAndFilter.options.appliedFilterCount;
  const sortAndFilterOptions = sortAndFilter && sortAndFilter.options && mapSortAndFilterOptions(sortAndFilter.options.sortAndFilter);

  return Object.assign({}, { appliedFilterCount }, sortAndFilterOptions);
};

const mapSortAndFilterOptions = (sortAndFilterOptions: PropertySearch._SortAndFilter[]): any =>
  Object.assign(
    {},
    ...sortAndFilterOptions.map(sortAndFilterOption => ({
      [sortAndFilterOption['name']]: {
        options: sortAndFilterOption.options.map(mapOption),
        label: sortAndFilterOption.label,
        max: sortAndFilterOption.max,
        min: sortAndFilterOption.min,
        range: sortAndFilterOption.isRange,
        placeholder: sortAndFilterOption.placeholder,
        subLabel: sortAndFilterOption.subLabel,
        step: sortAndFilterOption.step,
        name: sortAndFilterOption.name,
        hiddenThreshold: sortAndFilterOption.hiddenThreshold
      }
    }))
  );

const mapOption = (option: PropertySearch._Options): any => ({
  selected: option.isSelected,
  ...option,
  value: option.optionValue
});

const getPills = (filters: PropertySearch.SortAndFilter): PillProps[] => {
  const selectedFilters = filters.options.sortAndFilter.filter(filter => filter.selected);

  return flatMap(selectedFilters, (filter: PropertySearch._SortAndFilter) => getPillsFromFilter(filter, filters.selected.priceFilterPills));
};

const getPillsFromFilter = (filter: PropertySearch._SortAndFilter, pricePills: PropertySearch.PriceFilterPills): PillProps[] => {
  const { name, min, max } = filter;
  if (name === 'price' && !isPriceSliderFilter(min, max)) {
    return [getPriceBucketsFilterPill(pricePills)];
  } else {
    return filter.options.filter(option => option.isSelected).map(option => getPill(filter, option));
  }
};

const getPill = (filter: PropertySearch._SortAndFilter, option: PropertySearch._Options): PillProps => ({
  query: '',
  name: filter.name,
  label: option.selectedLabel,
  value: option.optionValue,
  generated: true
});

const getPriceBucketsFilterPill = (pricePills: PropertySearch.PriceFilterPills): PillProps => ({
  query: '',
  name: 'price',
  label: pricePills && pricePills.aggregate.label,
  value: pricePills && pricePills.aggregate.value,
  generated: true
});
